import { SagaIterator } from 'redux-saga';
import { all, call, delay, put, select, take, takeEvery, takeLatest } from 'redux-saga/effects';
import {
  AngebotsId,
  DoRequestResponse,
  getEndpointUrl,
  GlobalAction,
  GlobalActions,
  GlobalPut,
  ModalType,
  ResponseType,
  VorschlagDruckClicked,
} from '@ps20-frontend/shop-integration-redux';
import {
  DokumentDruckdaten,
  DokumentenErstellungRequestModel,
  PdfResponse,
  PKBDruckdaten,
  SparteDruckdaten,
  SpartenType,
  ThhHundDruckdaten,
  ThhPferdDruckdaten,
  TkvDruckdaten,
  VermittlerDruck,
  VersicherungsnehmerDruck,
  VorschlagDruckType,
} from '@ps20/bff-models';
import { App, getSpartenTypeByApp } from '@ps20-frontend/frontend-models';
import { AxiosError } from 'axios';
import { safeSaga } from '@ps20-frontend/safe-saga';
import { PS20Logger } from '@ps20-frontend/logging';
import { CheckoutAction, CheckoutActions } from '../checkout.actions';
import { IAppState } from '../localStore';
import { Dokument } from '../model/dokumente.enum';
import { Versicherungsnehmer } from '../reducer/versicherungsnehmer.reducer';
import { Zahlung } from '../reducer/zahlung.reducer';
import { DruckstatusEnum } from '../reducer/druckstatus.reducer';
import { shouldUseSdwEndpoint } from '../selector/transient-state.selector';
import { mapBeiblatt } from './druck/beiblatt.mapper';
import { mapInfopaket } from './druck/infopaket.mapper';
import { mapVorschlag } from './druck/vorschlag.mapper';
import { mapPKB, mapVermittlerWechselDaten } from './druck/pkb.mapper';
import {
  beiblattTypeForZahlungsweise,
  DokumentnameBestandteile,
  DruckDokumentSparte,
  DruckDokumentType,
  generateDokumentname,
  spartenTypeToDruckDokumentSparte,
} from './druckenSaga.helper';
import { handleBase64 } from './druck/handle-blob';
import { splitValidationMessage } from './pollingHelper/pollingHelper';
import { mapEinwilligung } from './druck/einwilligung.mapper';
import { mapJagdbestaetigung } from './druck/jagdbestaetigung.mapper';

export const BEIBLATT_REQUEST_SUCCESS = 'BEIBLATT_REQUEST_SUCCESS';
export const BEIBLATT_REQUEST_ERROR = 'BEIBLATT_REQUEST_ERROR';
export const WEWE_REQUEST_SUCCESS = 'WEWE_REQUEST_SUCCESS';
export const WEWE_REQUEST_ERROR = 'WEWE_REQUEST_ERROR';
export const INFOPAKET_VHV_REQUEST_SUCCESS = 'INFOPAKET_VHV_REQUEST_SUCCESS';
export const INFOPAKET_VWG_REQUEST_SUCCESS = 'INFOPAKET_VWG_REQUEST_SUCCESS';
export const INFOPAKET_VWG_V2_REQUEST_SUCCESS = 'INFOPAKET_VWG_V2_REQUEST_SUCCESS';
export const INFOPAKET_PH_REQUEST_SUCCESS = 'INFOPAKET_PH_REQUEST_SUCCESS';
export const INFOPAKET_RS_REQUEST_SUCCESS = 'INFOPAKET_RS_REQUEST_SUCCESS';
export const INFOPAKET_RS_VM_REQUEST_SUCCESS = 'INFOPAKET_RS_VM_REQUEST_SUCCESS';
export const INFOPAKET_THH_HUND_REQUEST_SUCCESS = 'INFOPAKET_THH_HUND_REQUEST_SUCCESS';
export const INFOPAKET_THH_PFERD_REQUEST_SUCCESS = 'INFOPAKET_THH_PFERD_REQUEST_SUCCESS';
export const INFOPAKET_TKV_REQUEST_SUCCESS = 'INFOPAKET_TKV_REQUEST_SUCCESS';
export const INFOPAKET_TKV_V2_REQUEST_SUCCESS = 'INFOPAKET_TKV_V2_REQUEST_SUCCESS';
export const INFOPAKET_URI_REQUEST_SUCCESS = 'INFOPAKET_URI_REQUEST_SUCCESS';
export const INFOPAKET_JHP_REQUEST_SUCCESS = 'INFOPAKET_JHP_REQUEST_SUCCESS';
export const INFOPAKET_GEGE_REQUEST_SUCCESS = 'INFOPAKET_GEGE_REQUEST_SUCCESS';
export const INFOPAKET_REQUEST_ERROR = 'INFOPAKET_REQUEST_ERROR';
export const PKB_VHV_REQUEST_SUCCESS = 'PKB_VHV_REQUEST_SUCCESS';
export const PKB_VWG_REQUEST_SUCCESS = 'PKB_VWG_REQUEST_SUCCESS';
export const PKB_VWG_V2_REQUEST_SUCCESS = 'PKB_VWG_V2_REQUEST_SUCCESS';
export const PKB_PH_REQUEST_SUCCESS = 'PKB_PH_REQUEST_SUCCESS';
export const PKB_URI_REQUEST_SUCCESS = 'PKB_URI_REQUEST_SUCCESS';
export const PKB_RS_REQUEST_SUCCESS = 'PKB_RS_REQUEST_SUCCESS';
export const PKB_RS_VERMIETUNG_REQUEST_SUCCESS = 'PKB_RS_VERMIETUNG_REQUEST_SUCCESS';
export const PKB_THH_PFERD_REQUEST_SUCCESS = 'PKB_THH_PFERD_REQUEST_SUCCESS';
export const PKB_THH_HUND_REQUEST_SUCCESS = 'PKB_THH_HUND_REQUEST_SUCCESS';
export const PKB_TKV_REQUEST_SUCCESS = 'PKB_TKV_REQUEST_SUCCESS';
export const PKB_TKV_V2_REQUEST_SUCCESS = 'PKB_TKV_V2_REQUEST_SUCCESS';
export const PKB_JHP_REQUEST_SUCCESS = 'PKB_JHP_REQUEST_SUCCESS';
export const PKB_GEGE_REQUEST_SUCCESS = 'PKB_GEGE_REQUEST_SUCCESS';
export const PKB_REQUEST_ERROR = 'PKB_REQUEST_ERROR';

export const JAGDBESTAETIGUNG_REQUEST_SUCCESS = 'JAGDBESTAETIGUNG_REQUEST_SUCCESS';
export const JAGDBESTAETIGUNG_REQUEST_ERROR = 'JAGDBESTAETIGUNG_REQUEST_ERROR';

export const VORSCHLAG_DRUCK_REQUEST_SUCCESS = 'VORSCHLAG_DRUCK_REQUEST_SUCCESS';
export const VORSCHLAG_DRUCK_REQUEST_ERROR = 'VORSCHLAG_DRUCK_REQUEST_ERROR';

export const SUPPORTED_DRUCKSTUECKE: Array<App> = [
  App.VWG,
  App.VWG_V2,
  App.VHV,
  App.VHV_V2,
  App.PH,
  App.RS,
  App.THH_PFERD,
  App.THH_HUND,
  App.TKV,
  App.TKV_V2,
  App.JHP,
  App.UNF,
  App.GEGE,
  App.RS_VERMIETUNG,
];

export function* watchDruckenLinkClicked(): SagaIterator {
  yield takeLatest(
    CheckoutActions.DRUCKEN_LINK_CLICKED,
    safeSaga(druckLinkClicked, App.SHOP_CHECKOUT),
  );
  yield takeLatest(
    CheckoutActions.TESTDOKUMENTE_DOWNLOAD_CLICKED,
    safeSaga(druckenTestdokumente, App.SHOP_CHECKOUT),
  );
  yield takeLatest(
    GlobalActions.VORSCHLAG_DRUCK_CLICKED,
    safeSaga(druckenVorschlag, App.SHOP_CHECKOUT),
  );
  yield takeLatest(
    GlobalActions.VORSCHLAG_DRUCK_DATEN_REQUESTED,
    safeSaga(generateVorschlagdruckdaten, App.SHOP_CHECKOUT),
  );
  yield takeEvery(
    VORSCHLAG_DRUCK_REQUEST_SUCCESS,
    safeSaga(druckEinzelPdfSuccessCallback, App.SHOP_CHECKOUT),
  );
  yield takeEvery(VORSCHLAG_DRUCK_REQUEST_ERROR, safeSaga(errorCallback, App.SHOP_CHECKOUT));
  yield takeEvery(
    BEIBLATT_REQUEST_SUCCESS,
    safeSaga(druckEinzelPdfSuccessCallback, App.SHOP_CHECKOUT),
  );
  yield takeEvery(WEWE_REQUEST_SUCCESS, safeSaga(druckEinzelPdfSuccessCallback, App.SHOP_CHECKOUT));
  yield takeEvery(INFOPAKET_REQUEST_ERROR, safeSaga(errorCallback, App.SHOP_CHECKOUT));
  yield takeEvery(BEIBLATT_REQUEST_ERROR, safeSaga(errorCallback, App.SHOP_CHECKOUT));
  yield takeEvery(WEWE_REQUEST_ERROR, safeSaga(errorCallback, App.SHOP_CHECKOUT));
  yield takeEvery(PKB_REQUEST_ERROR, safeSaga(errorCallback, App.SHOP_CHECKOUT));
  yield takeEvery(JAGDBESTAETIGUNG_REQUEST_ERROR, safeSaga(errorCallback, App.SHOP_CHECKOUT));
}

export function* druckLinkClicked(action: CheckoutAction<Dokument>): SagaIterator {
  const functionToCall = {
    [Dokument.INFOPAKET]: druckenInfopaket,
    [Dokument.BEIBLATT_MIT_SEPA]: druckenBeiblatt,
    [Dokument.BEIBLATT_OHNE_SEPA]: druckenBeiblatt,
    [Dokument.PKB]: druckenPKB,
    [Dokument.JAGDBESTAETIGUNG]: druckenJagdbestaetigung,
    [Dokument.WEWE]: druckenWEWE,
  }[action.payload];

  return yield functionToCall && call(functionToCall);
}

export function* druckenTestdokumente(): SagaIterator {
  try {
    yield call(druckenInfopaket);
  } catch (err) {
    PS20Logger.error(App.SHOP_CHECKOUT, `Fehler beim Drucken des Infopakets: ${err}`);
  }
  try {
    yield call(druckenPKB);
  } catch (err) {
    PS20Logger.error(App.SHOP_CHECKOUT, `Fehler beim Drucken der PKB: ${err}`);
  }
}

export function* getSpartenDruckdaten(): SagaIterator {
  const globalPut = GlobalPut.getInstance();
  const state: IAppState = yield select();
  const druckdaten: Array<SparteDruckdaten> = [];

  const produkteImWarenkorb: App[] = state.produkte
    .map(produkt => produkt.appId)
    .filter(produktId => SUPPORTED_DRUCKSTUECKE.includes(produktId));

  yield call(globalPut.put, new GlobalActions().requestAngeboteInWarenkorb());

  const angeboteInWarenkorbAction: GlobalAction<Array<AngebotsId>> = yield take(
    GlobalActions.ANGEBOTE_IN_WARENKORB_PROVIDED,
  );
  const produktOrder: Array<AngebotsId> = angeboteInWarenkorbAction.payload;

  yield call(globalPut.put, new GlobalActions().druckdatenRequested(produkteImWarenkorb));

  for (const _produkt of produkteImWarenkorb) {
    const druckdatenAction: GlobalAction<SparteDruckdaten> = yield take(
      GlobalActions.DRUCKDATEN_GENERATED,
    );
    druckdaten.push(druckdatenAction.payload);
  }

  return produktOrder
    .map(angebotsId => {
      return druckdaten.find(
        _druckdaten =>
          _druckdaten.spartenType === getSpartenTypeByApp(angebotsId.appId) &&
          _druckdaten.angebotsId === angebotsId.angebotsId,
      );
    })
    .filter(_druckdaten => _druckdaten != null);
}

export function* mapBeiblattDaten(spartenDruckdaten: Array<SparteDruckdaten>): SagaIterator {
  const state: IAppState = yield select();
  return mapBeiblatt(state, spartenDruckdaten);
}

export function* mapWeweDaten(spartenDruckdaten: Array<SparteDruckdaten>): SagaIterator {
  const state: IAppState = yield select();
  return mapEinwilligung(state, spartenDruckdaten);
}

export function* mapVorschlagsdaten(
  spartenDruckdaten: Array<SparteDruckdaten>,
  gesamtbeitragAllerSparten: string,
  vorschlagType?: VorschlagDruckType,
): SagaIterator {
  const state: IAppState = yield select();
  return mapVorschlag(state, gesamtbeitragAllerSparten, spartenDruckdaten, vorschlagType);
}

export function* druckenBeiblatt(): SagaIterator {
  const spartenDruckdaten: Array<SparteDruckdaten> = yield call(getSpartenDruckdaten);
  const request = yield call(mapBeiblattDaten, spartenDruckdaten);
  yield call(drucken, request, BEIBLATT_REQUEST_SUCCESS, BEIBLATT_REQUEST_ERROR);
}

export function* druckenWEWE(): SagaIterator {
  const spartenDruckdaten: Array<SparteDruckdaten> = yield call(getSpartenDruckdaten);
  const request = yield call(mapWeweDaten, spartenDruckdaten);
  yield call(drucken, request, WEWE_REQUEST_SUCCESS, WEWE_REQUEST_ERROR);
}

export function* mapInfopaketDaten(spartenDruckdaten: Array<SparteDruckdaten>): SagaIterator {
  const state: IAppState = yield select();
  return spartenDruckdaten.map(sparteDruckdaten => mapInfopaket(state, sparteDruckdaten));
}

export function* druckenVorschlag(action: GlobalAction<VorschlagDruckClicked>): SagaIterator {
  const spartenDruckdaten: Array<SparteDruckdaten> = yield call(getSpartenDruckdaten);
  const gesamtbeitragAllerSparten = action.payload.gesamtbeitragAllerSparten
    ?.toFixed(2)
    .replace('.', ',');
  const vorschlagType: VorschlagDruckType = action.payload.vorschlagType;

  const druckRequestModel = yield call(mapVorschlagsdaten,
    spartenDruckdaten,
    gesamtbeitragAllerSparten,
    vorschlagType,
  );

  // Entferne nicht benötigte Daten für Vorschlag.
  removeTierdaten(druckRequestModel.dokumentDruckdaten.sparten);
  removeBnrb(druckRequestModel.vermittler);
  removeEmail(druckRequestModel.versicherungsnehmer);

  yield call(
    drucken,
    druckRequestModel,
    VORSCHLAG_DRUCK_REQUEST_SUCCESS,
    VORSCHLAG_DRUCK_REQUEST_ERROR,
  );
}

export function removeTierdaten(druckdaten: Array<SparteDruckdaten>): void {
  druckdaten.forEach(druckdate => {
    switch (druckdate.spartenType) {
      case SpartenType.TIERHALTERHAFTPFLICHT_HUND:
        (druckdate as ThhHundDruckdaten).versicherteTiere.forEach(tier => {
          tier.tiername = undefined;
          tier.taetonummer = undefined;
          tier.geburtsdatum = undefined;
          tier.chipnummer = undefined;
          tier.geschlecht = undefined;
        });
        break;
      case SpartenType.TIERHALTERHAFTPFLICHT_PFERD:
        (druckdate as ThhPferdDruckdaten).versicherteTiere.forEach(tier => {
          tier.tiername = undefined;
          tier.geburtsdatum = undefined;
          tier.chipnummer = undefined;
          tier.lebensnummer = undefined;
          tier.geschlecht = undefined;
        });
        break;
      case SpartenType.TIERKRANKEN:
        (druckdate as TkvDruckdaten).versichertesTier.tiername = undefined;
        break;
    }
  });
}

export function removeBnrb(vermittler: VermittlerDruck): void {
  vermittler.bnrb = undefined;
}

export function removeEmail(versicherungsnehmer: VersicherungsnehmerDruck): void {
  versicherungsnehmer.email = undefined;
}

export function* generateVorschlagdruckdaten(action: GlobalAction<number>): SagaIterator {
  const spartenDruckdaten: Array<SparteDruckdaten> = yield call(getSpartenDruckdaten);
  const gesamtbeitragAllerSparten = action?.payload?.toFixed(2).replace('.', ',');
  const druckRequestModel = yield call(mapVorschlagsdaten,
    spartenDruckdaten,
    gesamtbeitragAllerSparten,
    VorschlagDruckType.LANG,
  );

  // Entferne nicht benötigte Daten für Vorschlag.
  removeTierdaten(druckRequestModel.dokumentDruckdaten.sparten);
  removeBnrb(druckRequestModel.vermittler);
  removeEmail(druckRequestModel.versicherungsnehmer);

  const globalPut = GlobalPut.getInstance();
  yield call(globalPut.put, new GlobalActions().vorschlagdruckdatenGenerated(druckRequestModel));
}

export function* druckenInfopaket(): SagaIterator {
  const spartenDruckdaten: Array<SparteDruckdaten> = yield call(getSpartenDruckdaten);
  const infopakete = yield call(mapInfopaketDaten, spartenDruckdaten);

  const successActions = [];
  for (const infopaket of infopakete) {
    const successEvent = getSuccessRequestAction(
      infopaket,
      infopakete,
      getInfopaketSuccessEventMap(),
    );
    successActions.push(successEvent);
    yield call(drucken, infopaket, successEvent, INFOPAKET_REQUEST_ERROR);
  }
  const pdfResponses = yield all(successActions.map(action => take(action)));
  const { versicherungsnehmer }: IAppState = yield select();

  pdfResponses.forEach((pdfResponse: DoRequestResponse<PdfResponse>) => {
    downloadPdf(DruckDokumentType.INFOPAKET, pdfResponse, versicherungsnehmer);
  });

  const globalPut = GlobalPut.getInstance();
  globalPut.put({ type: CheckoutActions.INFOPAKET_DRUCKEN_SUCCESS });
}

export function getSuccessRequestAction(
  dokumentRequest: DokumentenErstellungRequestModel<DokumentDruckdaten>,
  dokumentRequestArray: DokumentenErstellungRequestModel<DokumentDruckdaten>[],
  sparteToEventMap: { [key: string]: string },
): string | undefined {
  const dokumentRequestWithSameSparte = dokumentRequestArray.filter(
    _dokumentRequest =>
      _dokumentRequest.sparteDruckdaten.spartenType ===
      dokumentRequest.sparteDruckdaten.spartenType,
  );

  if (dokumentRequestWithSameSparte.length > 1) {
    return (
      sparteToEventMap[dokumentRequest.sparteDruckdaten.spartenType] +
      dokumentRequestWithSameSparte.findIndex(elem => elem === dokumentRequest).toString()
    );
  }

  return sparteToEventMap[dokumentRequest.sparteDruckdaten.spartenType];
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function getInfopaketSuccessEventMap(): any {
  return {
    [SpartenType.WOHNGEBAEUDE]: INFOPAKET_VWG_REQUEST_SUCCESS,
    [SpartenType.WOHNGEBAEUDE_V2]: INFOPAKET_VWG_V2_REQUEST_SUCCESS,
    [SpartenType.HAUSRAT]: INFOPAKET_VHV_REQUEST_SUCCESS,
    [SpartenType.HAUSRAT_V2]: INFOPAKET_VHV_REQUEST_SUCCESS,
    [SpartenType.PRIVATHAFTPFLICHT]: INFOPAKET_PH_REQUEST_SUCCESS,
    [SpartenType.RECHTSSCHUTZ]: INFOPAKET_RS_REQUEST_SUCCESS,
    [SpartenType.RECHTSSCHUTZ_VERMIETUNG]: INFOPAKET_RS_VM_REQUEST_SUCCESS,
    [SpartenType.TIERHALTERHAFTPFLICHT_HUND]: INFOPAKET_THH_HUND_REQUEST_SUCCESS,
    [SpartenType.TIERHALTERHAFTPFLICHT_PFERD]: INFOPAKET_THH_PFERD_REQUEST_SUCCESS,
    [SpartenType.TIERKRANKEN]: INFOPAKET_TKV_REQUEST_SUCCESS,
    [SpartenType.TIERKRANKEN_V2]: INFOPAKET_TKV_V2_REQUEST_SUCCESS,
    [SpartenType.UNFALL]: INFOPAKET_URI_REQUEST_SUCCESS,
    [SpartenType.JAGDHAFTPFLICHT]: INFOPAKET_JHP_REQUEST_SUCCESS,
    [SpartenType.GEGENSTANDSSCHUTZ]: INFOPAKET_GEGE_REQUEST_SUCCESS,
  };
}

export function* druckenJagdbestaetigung(): SagaIterator {
  const state: IAppState = yield select();
  const spraten: Array<SparteDruckdaten> = yield call(getSpartenDruckdaten);
  const sprateDruckdaten = spraten.find(
    sparte => sparte.spartenType === SpartenType.JAGDHAFTPFLICHT,
  );
  const jagdbestaetigung = mapJagdbestaetigung(state, sprateDruckdaten);
  yield call(
    drucken,
    jagdbestaetigung,
    JAGDBESTAETIGUNG_REQUEST_SUCCESS,
    JAGDBESTAETIGUNG_REQUEST_ERROR,
  );
  const pdfResponse = yield take(JAGDBESTAETIGUNG_REQUEST_SUCCESS);
  const { versicherungsnehmer }: IAppState = yield select();
  downloadPdf(DruckDokumentType.JAGDBESTAETIGUNG, pdfResponse, versicherungsnehmer);
  GlobalPut.getInstance().put({ type: CheckoutActions.JAGDBESTAETIGUNG_DRUCKEN_SUCCESS });
}

export function* druckenPKB(): SagaIterator {
  const state: IAppState = yield select();
  const spartenDruckdaten: Array<SparteDruckdaten> = yield call(getSpartenDruckdaten);
  const pkbs: Array<DokumentenErstellungRequestModel<PKBDruckdaten>> = spartenDruckdaten.map(
    sparteDruckdaten =>
      mapPKB(state, sparteDruckdaten, mapVermittlerWechselDaten(spartenDruckdaten)),
  );
  const successActions = [];

  for (const pkb of pkbs) {
    const successEvent = getSuccessRequestAction(pkb, pkbs, getSparteToPkbSuccessEventMap());
    successActions.push(successEvent);
    yield call(drucken, pkb, successEvent, PKB_REQUEST_ERROR);
  }
  const pdfResponses: Array<DoRequestResponse<PdfResponse>> = yield all(
    successActions.map(action => take(action)),
  );
  const { versicherungsnehmer }: IAppState = yield select();

  for (const pdfResponse of pdfResponses) {
    downloadPdf(DruckDokumentType.PKB, pdfResponse, versicherungsnehmer);
    yield delay(1000);
  }

  GlobalPut.getInstance().put({ type: CheckoutActions.PKB_DRUCKEN_SUCCESS });
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function getSparteToPkbSuccessEventMap(): any {
  return {
    [SpartenType.WOHNGEBAEUDE]: PKB_VWG_REQUEST_SUCCESS,
    [SpartenType.WOHNGEBAEUDE_V2]: PKB_VWG_V2_REQUEST_SUCCESS,
    [SpartenType.HAUSRAT]: PKB_VHV_REQUEST_SUCCESS,
    [SpartenType.HAUSRAT_V2]: PKB_VHV_REQUEST_SUCCESS,
    [SpartenType.PRIVATHAFTPFLICHT]: PKB_PH_REQUEST_SUCCESS,
    [SpartenType.UNFALL]: PKB_URI_REQUEST_SUCCESS,
    [SpartenType.RECHTSSCHUTZ]: PKB_RS_REQUEST_SUCCESS,
    [SpartenType.RECHTSSCHUTZ_VERMIETUNG]: PKB_RS_VERMIETUNG_REQUEST_SUCCESS,
    [SpartenType.TIERHALTERHAFTPFLICHT_HUND]: PKB_THH_HUND_REQUEST_SUCCESS,
    [SpartenType.TIERHALTERHAFTPFLICHT_PFERD]: PKB_THH_PFERD_REQUEST_SUCCESS,
    [SpartenType.TIERKRANKEN]: PKB_TKV_REQUEST_SUCCESS,
    [SpartenType.TIERKRANKEN_V2]: PKB_TKV_V2_REQUEST_SUCCESS,
    [SpartenType.JAGDHAFTPFLICHT]: PKB_JHP_REQUEST_SUCCESS,
    [SpartenType.GEGENSTANDSSCHUTZ]: PKB_GEGE_REQUEST_SUCCESS,
  };
}

export const downloadPdf = function (
  doukumentType: DruckDokumentType,
  pdfBlob: DoRequestResponse<PdfResponse>,
  versicherungsnehmer: Versicherungsnehmer,
) {
  const request: DokumentenErstellungRequestModel<DokumentDruckdaten> = pdfBlob.doRequestAction
    .payload.body as DokumentenErstellungRequestModel<DokumentDruckdaten>;

  const dokumentenName = generateDrucknameForDokumentType(
    doukumentType,
    spartenTypeToDruckDokumentSparte(request.sparteDruckdaten.spartenType),
    versicherungsnehmer,
  );

  handleBase64(pdfBlob.payload.bytes, dokumentenName);
};

function* drucken(
  request: DokumentenErstellungRequestModel<DokumentDruckdaten>,
  successEvent: string,
  errorEvent: string,
): SagaIterator {
  const state: IAppState = yield select();

  const globalPut = GlobalPut.getInstance();
  yield call(
    globalPut.put.bind(globalPut),
    new GlobalActions().doRequest({
      appId: App.SHOP_CHECKOUT,
      body: request,
      url: getEndpointUrl(shouldUseSdwEndpoint(state), '/privatschutz/api/dokumentenerstellung'),
      successEvent,
      errorEvent,
      responseType: ResponseType.APPLICATION_JSON,
    }),
  );
}

export function* druckEinzelPdfSuccessCallback(successAction: DoRequestResponse<PdfResponse>): SagaIterator {
  let actionType = '';

  switch (successAction.type) {
    case BEIBLATT_REQUEST_SUCCESS:
      actionType = CheckoutActions.BEIBLATT_DRUCKEN_SUCCESS;
      break;
    case WEWE_REQUEST_SUCCESS:
      actionType = CheckoutActions.WEWE_DRUCKEN_SUCCESS;
      break;
    case VORSCHLAG_DRUCK_REQUEST_SUCCESS:
      actionType = GlobalActions.VORSCHLAG_DRUCKEN_SUCCESS;
      break;
  }

  const { versicherungsnehmer, zahlung }: IAppState = yield select();
  const dokumentenName = generateDokumentnameForBeiblattOrVorschlag(
    successAction.type,
    versicherungsnehmer,
    zahlung,
  );

  handleBase64(successAction.payload.bytes, dokumentenName);
  const globalPut = GlobalPut.getInstance();
  globalPut.put({ type: actionType });
}

export function* errorCallback(errorAction: DoRequestResponse<AxiosError>): SagaIterator {
  switch (errorAction.type) {
    case BEIBLATT_REQUEST_ERROR:
      yield put(new CheckoutActions().beiblattDruckenError(DruckstatusEnum.ERROR));
      break;
    case INFOPAKET_REQUEST_ERROR:
      yield put(new CheckoutActions().infopaketDruckenError(DruckstatusEnum.ERROR));
      break;
    case VORSCHLAG_DRUCK_REQUEST_ERROR:
      if (
        errorAction.payload instanceof AxiosError &&
        errorAction.payload.response?.status === 400
      ) {
        yield call(
          GlobalPut.getInstance().put,
          new GlobalActions().vorschlagDruckenError(DruckstatusEnum.ERROR),
        );
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const { additionalInformation }: { [p: string]: any } = errorAction.payload.response.data;
        yield call(
          GlobalPut.getInstance().put,
          new GlobalActions().requestModal(ModalType.VALIDATION_ERROR, {
            body: splitValidationMessage(additionalInformation),
            button: 'Schließen',
          }),
        );
      } else {
        yield call(
          GlobalPut.getInstance().put,
          new GlobalActions().vorschlagDruckenError(DruckstatusEnum.ERROR),
        );
        yield call(
          GlobalPut.getInstance().put,
          new GlobalActions().requestModal(ModalType.VORSCHLAG_DRUCK_ERROR),
        );
      }
      break;
    case PKB_REQUEST_ERROR:
      yield put(new CheckoutActions().pkbDruckenError(DruckstatusEnum.ERROR));
      break;
    case JAGDBESTAETIGUNG_REQUEST_ERROR:
      yield put(new CheckoutActions().jagdbestaetigungDruckenError(DruckstatusEnum.ERROR));
  }
}

export function generateDokumentnameForBeiblattOrVorschlag(
  druckrequestType: string,
  versicherungsnehmer: Versicherungsnehmer,
  zahlung: Zahlung,
): string | undefined {
  return {
    [BEIBLATT_REQUEST_SUCCESS]: generateDrucknameForDokumentType(
      beiblattTypeForZahlungsweise(zahlung.zahlungsart),
      null,
      versicherungsnehmer,
    ),
    [WEWE_REQUEST_SUCCESS]: generateDrucknameForDokumentType(
      DruckDokumentType.WEWE,
      null,
      versicherungsnehmer,
    ),
    [VORSCHLAG_DRUCK_REQUEST_SUCCESS]: generateDrucknameForDokumentType(
      DruckDokumentType.VORSCHLAG,
      null,
      versicherungsnehmer,
    ),
  }[druckrequestType];
}

function extractDokumentnameBestandteile(
  type: DruckDokumentType,
  spartenName: DruckDokumentSparte,
  versicherungsnehmer: Versicherungsnehmer,
): DokumentnameBestandteile {
  return {
    type,
    spartenName,
    vorname: versicherungsnehmer.vorname,
    nachname: versicherungsnehmer.nachname,
    adelsbez: versicherungsnehmer.adelsbez ? versicherungsnehmer.adelsbez.text : null,
  };
}

export const generateDrucknameForDokumentType = function (
  type: DruckDokumentType,
  spartenName: DruckDokumentSparte,
  versicherungsnehmer: Versicherungsnehmer,
): string {
  const dokumentNameBestandteile = extractDokumentnameBestandteile(
    type,
    spartenName,
    versicherungsnehmer,
  );
  return generateDokumentname(dokumentNameBestandteile);
};
